import { t } from "i18next"
import { convertToReadableString, localisedValue } from "utils"
import { Card, Flex, Group, Skeleton, Text } from "components/common"
import getPrivilege from "utils/privileges/privileges"

/** Dynamic options for “next proposed task” come from API instead of `field.options`. */
export const NEXT_PROPOSED_TASK_KEY = "next_proposed_task"

/** Stable path for `Form.useWatch` when a field has no parent dependency (avoids `undefined` name). */
export const NO_DEP_WATCH = "no_dep"

/** Temporary: hide validity field until BE drives visibility (remove when API owns it). */
export const HIDDEN_ENTITY_VALID_FIELD_KEY = "is_entity_valid"

export const isDependencySatisfied = (parentValue, dependent) => {
  if (!dependent?.field) return true
  return Boolean(parentValue)
}

export const isEffectivelyMandatory = field => {
  if (field?.dependent?.field) return true
  return !!field?.mandatory
}

export const getFieldRules = field => {
  const rules = []
  const mandatory = isEffectivelyMandatory(field)
  if (mandatory && field?.fieldType === "scale" && field?.criteria?.length) {
    const criteriaLabels = field?.criteria?.map(c => localisedValue({ object: c, key: "label" }))

    rules.push({
      validator: (_, value) => {
        const obj = value && typeof value === "object" ? value : {}
        const allFilled = criteriaLabels.every(
          label => obj[label] != null && obj[label] !== "" && String(obj[label]).trim() !== ""
        )
        if (!allFilled) {
          return Promise.reject(new Error(t("{{label}} is required", { label: field?.title })))
        }
        return Promise.resolve()
      },
    })
  } else if (mandatory) {
    rules.push({
      required: true,
      message: t("{{label}} is required", { label: field?.title }),
    })
  }
  return rules
}

export const getReviewFormItemConfig = field => {
  const title = localisedValue({ object: field, key: "title" })
  return {
    label: field?.fieldType !== "checkbox" && title,
    rules: getFieldRules(field),
    required: isEffectivelyMandatory(field),
    hidden: field?.key === HIDDEN_ENTITY_VALID_FIELD_KEY,
    extra: field?.maxLength ? t("Max {{count}} characters", { count: field.maxLength }) : undefined,
    valuePropName: field?.fieldType === "toggle" || field?.fieldType === "checkbox" ? "checked" : undefined,
  }
}

export const REVIEW_TYPE_CONFIG = (userPrivileges = {}) => [
  {
    key: "lead_review",
    label: t("Manager Review"),
    value: "lead_review",
    disabled: !getPrivilege("can_add_lead_review", userPrivileges),
  },
  {
    key: "audit_review",
    value: "audit_review",
    label: t("Compliance Audit"),
    disabled: !getPrivilege("can_add_audit_review", userPrivileges),
  },
]

export const interestOptions = () => [
  { label: t("Interested"), key: "interested", value: true },
  { label: t("Not Interested"), key: "notInterested", value: false },
]

export const interestOptionsFilter = () => [
  { label: t("Interested"), value: "true" },
  { label: t("Not Interested"), value: "false" },
]

export const ENTITY_OPTIONS = () => [
  { key: "client", label: t("Client"), value: "client" },
  { key: "lead", label: t("Lead"), value: "lead" },
  { key: "meeting", label: t("Meeting"), value: "meeting" },
  { key: "call", label: t("Call"), value: "call" },
]

export const showValidityField = {
  call: true,
  meeting: true,
}

export const getSectionTitle = sectionKey => {
  const titles = {
    staff_evaluation: t("Staff Evaluation"),
    client_evaluation: t("Client Evaluation"),
    technical_issues: t("Technical Issues"),
    severity_escalation: t("Severity & Escalation"),
    action: t("Action"),
  }
  return titles[sectionKey] ?? convertToReadableString(sectionKey)
}

export const ADD_REVIEW_FORM_RULES = () => ({
  reviewType: [{ required: true, message: t("Review type is required") }],
  entityToReview: [{ required: true, message: t("Entity to review is required") }],
  meetingId: [{ required: true, message: t("Entity is required") }],
  isEntityValid: [{ required: true, message: t("Validity is required") }],
})

export const ReviewFormQuestionsSkeleton = () => {
  const sectionTitle = () => (
    <Skeleton type="antTitle" titleHeight="20px" titleWidth="200px" className="mbe-16" active />
  )
  const fieldRow = () => (
    <div className="mbe-20">
      <div className="mbe-8">
        <Skeleton type="label" width="40%" active />
      </div>
      <Skeleton type="input" height={40} active />
    </div>
  )
  const radioRow = () => (
    <div className="mbe-20">
      <div className="mbe-8">
        <Skeleton type="label" width="35%" active />
      </div>
      <Flex gap="12px" wrap="wrap" style={{ alignItems: "center" }}>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} type="radioButton" width={72} height={32} active />
        ))}
      </Flex>
    </div>
  )

  return (
    <Group gap="20px" template="1fr">
      <Text type="secondary" className="mbe-8" style={{ display: "block" }}>
        {t("Loading review questions…")}
      </Text>
      {sectionTitle()}
      {radioRow()}
      {fieldRow()}
      {fieldRow()}
      {sectionTitle()}
      {fieldRow()}
      {radioRow()}
      {fieldRow()}
      {fieldRow()}
    </Group>
  )
}

export const ReviewReportSkeleton = () => {
  const sectionTitle = () => (
    <Skeleton type="antTitle" titleHeight="20px" titleWidth="200px" className="mbe-16" active />
  )

  const keyValueRow = (labelWidth = "45%") => (
    <div className="mbe-12" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
      <Skeleton type="label" width={labelWidth} active />
      <Skeleton height={24} width={120} active />
    </div>
  )

  const pillRow = () => (
    <div className="mbe-12" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Skeleton type="label" width="35%" active />
      <Skeleton height={28} width={140} style={{ borderRadius: 9999 }} active />
    </div>
  )

  const scoringBlock = (rowCount = 5) => (
    <div className="mbe-16" style={{ padding: 16, background: "var(--gray100, #f5f5f5)", borderRadius: 8 }}>
      {Array.from({ length: rowCount }).map((_, i) => (
        <div
          key={i}
          className="mbe-10"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Skeleton type="label" width="60%" active />
          <Skeleton height={20} width={32} active />
        </div>
      ))}
    </div>
  )

  const textBlock = () => (
    <div className="mbe-16">
      <div className="mbe-8">
        <Skeleton type="label" width="30%" active />
      </div>
      <Skeleton type="input" height={64} active />
    </div>
  )
  return (
    <Group gap="24px" template="1fr">
      {/* Main title */}
      <Skeleton type="antTitle" titleHeight="28px" titleWidth="220px" className="mbe-8" active />

      {/* Status banner */}
      <Skeleton height={48} style={{ borderRadius: 8 }} active className="mbe-24" />

      {/* First section */}
      {sectionTitle()}
      {pillRow()}
      {pillRow()}
      {keyValueRow("40%")}
      {scoringBlock(6)}
      {pillRow()}
      {pillRow()}
      {textBlock()}

      {/* Second section */}
      {sectionTitle()}
      {keyValueRow()}
      {keyValueRow()}

      {/* Third section */}
      {sectionTitle()}
      {keyValueRow()}
      {textBlock()}
    </Group>
  )
}

export const ReviewDetailCardSkeleton = () => (
  <Card
    Shadow={false}
    bordered={false}
    bodyStyle={{ padding: 16 }}
    style={{ marginTop: 16, backgroundColor: "#F4FBF6", borderRadius: 8 }}
  >
    <Group gap="12px" template="1fr">
      <Skeleton type="antTitle" titleHeight="20px" titleWidth="200px" active />
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="mbe-4">
          <div className="mbe-4">
            <Skeleton type="label" width="38%" active />
          </div>
          <Skeleton height={20} width={`${55 + (i % 3) * 12}%`} active />
        </div>
      ))}
    </Group>
  </Card>
)
